import { useEffect, useRef } from "react";
import { experience } from "../data/index";

function ExperienceCard({ job, index }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateX(0)";
          }
        });
      },
      { threshold: 0.1 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      style={{
        opacity: 0,
        transform: "translateX(-40px)",
        transition: `all 0.7s ease ${index * 0.15}s`,
        display: "flex",
        gap: "32px",
      }}
    >
      {/* timeline line + dot */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: "16px",
            height: "16px",
            background: "#22d3ee",
            borderRadius: "50%",
            border: "3px solid #1a1a1a",
            boxShadow: "0 0 12pxrgba(34, 211, 238),0.6)",
            flexShrink: 0,
            marginTop: "6px",
          }}
        />
        <div
          style={{
            width: "2px",
            flex: 1,
            background: "linear-gradient(to bottom, #22d3ee, #2e2e2e)",
            marginTop: "8px",
            minHeight: "60px",
          }}
        />
      </div>

      {/* content */}
      <div
        style={{
          background: "#242424",
          border: "1px solid #2e2e2e",
          borderRadius: "16px",
          padding: "32px",
          flex: 1,
          marginBottom: "24px",
          transition: "all 0.3s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "#22d3ee";
          e.currentTarget.style.boxShadow =
            "0 12px 40pxrgba(34, 211, 238),0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "#2e2e2e";
          e.currentTarget.style.boxShadow = "none";
        }}
      >
        {/* role + company */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "8px",
          }}
        >
          <div>
            <h3
              style={{
                color: "#f5f5f5",
                fontSize: "1.15rem",
                fontWeight: 800,
                marginBottom: "4px",
                letterSpacing: "-0.3px",
              }}
            >
              {job.role}
            </h3>
            <p
              style={{
                color: "#22d3ee",
                fontSize: "0.95rem",
                fontWeight: 600,
              }}
            >
              {job.company}
            </p>
          </div>

          <div style={{ textAlign: "right" }}>
            <span
              style={{
                background: "rgba(57,255,20,0.08)",
                border: "1px solidrgba(34, 211, 238),0.2)",
                color: "#22d3ee",
                fontSize: "0.78rem",
                fontWeight: 600,
                padding: "4px 12px",
                borderRadius: "50px",
                display: "inline-block",
                marginBottom: "6px",
              }}
            >
              {job.period}
            </span>
            <p
              style={{
                color: "#666",
                fontSize: "0.8rem",
                textAlign: "right",
              }}
            >
              📍 {job.location}
            </p>
          </div>
        </div>

        {/* bullets */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: "20px",
            paddingTop: "20px",
            borderTop: "1px solid #2e2e2e",
          }}
        >
          {job.bullets.map((bullet) => (
            <div
              key={bullet}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <span
                style={{
                  color: "#22d3ee",
                  fontSize: "0.85rem",
                  marginTop: "3px",
                  flexShrink: 0,
                }}
              >
                ▸
              </span>
              <span
                style={{
                  color: "#a0a0a0",
                  fontSize: "0.9rem",
                  lineHeight: 1.7,
                }}
              >
                {bullet}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Experience() {
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = "1";
            entry.target.style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.2 },
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experience"
      style={{
        padding: "120px 24px",
        background: "#1e1e1e",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <div
          ref={headerRef}
          style={{
            opacity: 0,
            transform: "translateY(30px)",
            transition: "all 0.7s ease",
            marginBottom: "70px",
          }}
        >
          <p
            style={{
              color: "#22d3ee",
              fontSize: "0.85rem",
              fontWeight: 600,
              letterSpacing: "3px",
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            Experience
          </p>

          <h2
            style={{
              fontSize: "clamp(2rem, 4vw, 2.8rem)",
              fontWeight: 800,
              color: "#f5f5f5",
              marginBottom: "16px",
              letterSpacing: "-1px",
            }}
          >
            7+ years of shipping real software
          </h2>

          <p
            style={{
              color: "#a0a0a0",
              fontSize: "1.05rem",
              maxWidth: "560px",
              lineHeight: 1.8,
            }}
          >
            From IT support to software engineer, every role has made me a
            sharper and more deliberate engineer.
          </p>
        </div>

        {/* timeline */}
        <div style={{ maxWidth: "800px" }}>
          {experience.map((job, index) => (
            <ExperienceCard key={job.id} job={job} index={index} />
          ))}
        </div>

        {/* education card */}
        <div
          style={{
            maxWidth: "800px",
            marginTop: "20px",
            background: "#242424",
            border: "1px solid #2e2e2e",
            borderRadius: "16px",
            padding: "32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <p
              style={{
                color: "#22d3ee",
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "2px",
                textTransform: "uppercase",
                marginBottom: "8px",
              }}
            >
              🎓 Education
            </p>
            <h4
              style={{
                color: "#f5f5f5",
                fontWeight: 800,
                fontSize: "1.05rem",
                marginBottom: "4px",
              }}
            >
              BSc Computer Science and Engineering
            </h4>
            <p style={{ color: "#a0a0a0", fontSize: "0.9rem" }}>
              University of Information Science and Technology
            </p>
            <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "4px" }}>
              Ohrid, North Macedonia • 2011 – 2015
            </p>
          </div>

          <div
            style={{
              background: "rgba(57,255,20,0.08)",
              border: "1px solidrgba(34, 211, 238),0.2)",
              borderRadius: "12px",
              padding: "16px 24px",
              textAlign: "center",
            }}
          >
            <p
              style={{
                color: "#22d3ee",
                fontWeight: 800,
                fontSize: "1.4rem",
                marginBottom: "4px",
              }}
            >
              2015
            </p>
            <p style={{ color: "#666", fontSize: "0.8rem" }}>Graduated</p>
          </div>
        </div>
      </div>
    </section>
  );
}
